import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiJson } from './client';

export interface Group {
  id: string;
  userId: string;
  name: string;
  slug: string;
  order: number;
}
export interface LinkSet {
  id: string;
  userId: string;
  groupId: string;
  name: string;
  slug: string;
  order: number;
}
export interface Link {
  id: string;
  setId: string;
  canonicalUrlId: string;
  slug: string;
  customTitle: string;
  customFavicon: string | null;
  privateNotes: string;
  order: number;
  accessCount: number;
  canonicalUrl: { url: string; normalizedUrl: string; canonicalTitle: string; iconId: string | null };
}

export const keys = {
  groups: () => ['groups'] as const,
  setsInGroup: (slug: string) => ['groups', slug, 'sets'] as const,
  linksInSet: (slug: string) => ['sets', slug, 'links'] as const,
  resolve: (group?: string, set?: string) => ['resolve', group ?? '', set ?? ''] as const,
};

export function useGroups() {
  return useQuery({ queryKey: keys.groups(), queryFn: () => api.get('groups').json<Group[]>() });
}
export function useSets(groupSlug: string | null) {
  return useQuery({
    queryKey: keys.setsInGroup(groupSlug ?? ''),
    queryFn: () => api.get(`groups/${groupSlug}/sets`).json<LinkSet[]>(),
    enabled: !!groupSlug,
  });
}
export function useLinks(setSlug: string | null) {
  return useQuery({
    queryKey: keys.linksInSet(setSlug ?? ''),
    queryFn: () => api.get(`sets/${setSlug}/links`).json<Link[]>(),
    enabled: !!setSlug,
  });
}

export function useResolve(group?: string, set?: string) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: keys.resolve(group, set),
    queryFn: async () => {
      const path = set && group ? `resolve/${group}/${set}` : group ? `resolve/${group}` : 'resolve';
      try {
        return await api.get(path).json<{ groupSlug: string | null; setSlug: string | null }>();
      } catch (err) {
        // Offline fallback: the server-side resolver decides based on device
        // state, which we can't replicate exactly offline - but the offline
        // cache has enough to pick a sensible target. Prefer the URL params,
        // fall back to the first group/set we have cached. Better a slightly
        // stale landing than a stuck "Loading…" spinner.
        if (typeof navigator !== 'undefined' && navigator.onLine === false) {
          const cachedGroups = qc.getQueryData<Group[]>(keys.groups()) ?? [];
          const fallbackGroup =
            group ?? cachedGroups[0]?.slug ?? null;
          let fallbackSet = set ?? null;
          if (!fallbackSet && fallbackGroup) {
            const cachedSets =
              qc.getQueryData<LinkSet[]>(keys.setsInGroup(fallbackGroup)) ?? [];
            fallbackSet = cachedSets[0]?.slug ?? null;
          }
          return { groupSlug: fallbackGroup, setSlug: fallbackSet };
        }
        throw err;
      }
    },
    staleTime: 0,
    // Keep cached resolver results for offline rehydration of the last-seen
    // redirect, even though we don't persist this queryKey.
    retry: 0,
  });
}

export function useCommitLocation() {
  return useMutation({
    mutationFn: (input: { groupSlug?: string; setSlug?: string }) =>
      apiJson<{ success: boolean }>('resolve/commit', { method: 'POST', body: JSON.stringify(input) }),
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => apiJson<Group>('groups', { method: 'POST', body: JSON.stringify({ name }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.groups() }),
  });
}
export function useRenameGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { slug: string; name: string }) =>
      apiJson<Group>(`groups/${p.slug}`, { method: 'PATCH', body: JSON.stringify({ name: p.name }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.groups() }),
  });
}
export function useDeleteGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (slug: string) => apiJson<{ success: true }>(`groups/${slug}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.groups() }),
  });
}

export function useCreateSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { groupSlug: string; name: string }) =>
      apiJson<LinkSet>('sets', { method: 'POST', body: JSON.stringify(p) }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: keys.setsInGroup(v.groupSlug) }),
  });
}
export function useRenameSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { slug: string; name: string; groupSlug: string }) =>
      apiJson<LinkSet>(`sets/${p.slug}`, { method: 'PATCH', body: JSON.stringify({ name: p.name }) }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: keys.setsInGroup(v.groupSlug) }),
  });
}
export function useDeleteSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { slug: string; groupSlug: string }) =>
      apiJson<{ success: true }>(`sets/${p.slug}`, { method: 'DELETE' }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: keys.setsInGroup(v.groupSlug) }),
  });
}

export function useCreateLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { setSlug: string; url: string; title?: string; customFavicon?: string; privateNotes?: string }) =>
      apiJson<Link>('links', { method: 'POST', body: JSON.stringify(p) }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: keys.linksInSet(v.setSlug) }),
  });
}
export function useEditLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { slug: string; setSlug: string; customTitle?: string; customFavicon?: string; privateNotes?: string }) =>
      apiJson<Link>(`links/${p.slug}`, {
        method: 'PATCH',
        body: JSON.stringify({ customTitle: p.customTitle, customFavicon: p.customFavicon, privateNotes: p.privateNotes }),
      }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: keys.linksInSet(v.setSlug) }),
  });
}
export function useDeleteLink() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { slug: string; setSlug: string }) =>
      apiJson<{ success: true }>(`links/${p.slug}`, { method: 'DELETE' }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: keys.linksInSet(v.setSlug) }),
  });
}
export function useRecordLinkAccess() {
  return useMutation({
    mutationFn: (slug: string) => apiJson<{ success: true }>(`links/${slug}/access`, { method: 'POST', body: '{}' }),
  });
}

export function useReorderLinks() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { setSlug: string; items: Array<{ slug: string; order: number }> }) =>
      apiJson<{ success: true }>('links/reorder', { method: 'POST', body: JSON.stringify(p) }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: keys.linksInSet(v.setSlug) }),
  });
}

export function useReorderSets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: { groupSlug: string; items: Array<{ slug: string; order: number }> }) =>
      apiJson<{ success: true }>('sets/reorder', { method: 'POST', body: JSON.stringify(p) }),
    onSuccess: (_d, v) => qc.invalidateQueries({ queryKey: keys.setsInGroup(v.groupSlug) }),
  });
}

export function useReorderGroups() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (items: Array<{ slug: string; order: number }>) =>
      apiJson<{ success: true }>('groups/reorder', { method: 'POST', body: JSON.stringify({ items }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.groups() }),
  });
}
